import { CheckIcon, Loader2Icon, MicIcon, XIcon } from 'lucide-react'
import {
	type FC,
	type MouseEvent,
	type PointerEvent,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react'
import { Button } from '#app/components/ui/button'
import { cn } from '#app/utils/misc.tsx'
import {
	type VoiceInputDetails,
	type VoiceInputOutcome,
} from '#app/utils/plausible.client.ts'
import { createVoiceActivity } from '#app/utils/voice-activity.ts'
import {
	currentMicPermission,
	markMicHelpShown,
	micErrorDetail,
	micErrorName,
	micHelpShownBefore,
	micPermissionAfter,
	micWaitBucket,
	nextMicAttempt,
	primeMicPermission,
} from '#app/utils/voice-input-diagnostics.ts'

/**
 * Hård maxtid per inspelning. Röstfrågor är typiskt 3-8 s; 20 s räcker till
 * en lång fråga men stänger dörren för dikterade utläggningar.
 */
export const MAX_RECORDING_SECONDS = 20
/** Nedräkningen syns först när så här många sekunder återstår. */
const SHOW_COUNTDOWN_AT = 5
/** Längre tryck än så här = håll-in-läge (prata medan du håller, släpp = klar). */
const HOLD_MS = 400

type Status = 'idle' | 'starting' | 'recording' | 'transcribing'
/** pending = fingret är nere och vi vet ännu inte om det är tryck eller håll. */
type Mode = 'pending' | 'toggle' | 'hold'

/** Chrome/Firefox spelar in webm/opus, Safari mp4/aac. */
function pickMimeType() {
	if (typeof MediaRecorder === 'undefined') return undefined
	return [
		'audio/webm;codecs=opus',
		'audio/webm',
		'audio/mp4',
		'audio/ogg',
	].find(t => MediaRecorder.isTypeSupported(t))
}

export function voiceInputSupported() {
	return (
		typeof window !== 'undefined' &&
		typeof MediaRecorder !== 'undefined' &&
		!!navigator.mediaDevices?.getUserMedia
	)
}

const NO_SPEECH = 'Hörde inget tal. Försök igen.'
/** Efter ett nej: vägen tillbaka (IM-128). Chrome har en ikon till vänster
 * om adressen (Behörigheter), Safari aA (Webbplatsinställningar). */
const MIC_NOT_ALLOWED =
	'Mikrofonen är inte tillåten. Tillåt den via ikonen till vänster om adressen (aA i Safari) → Mikrofon och försök igen.'
/** Chromes "Permission denied by system": telefonen har stängt av mikrofonen
 * för webbläsarappen, så sajtens inställning hjälper inte. */
const MIC_OFF_IN_SYSTEM =
	'Telefonen har stängt av mikrofonen för webbläsaren. Slå på den under telefonens inställningar för appen och försök igen.'
/** Texterna som visar vägen tillbaka - räknas som hjälptexten i spårningen. */
const MIC_HELP = [MIC_NOT_ALLOWED, MIC_OFF_IN_SYSTEM]
const MIC_NOT_FOUND = 'Hittade ingen mikrofon. Skriv frågan i stället.'
const MIC_BUSY =
	'Mikrofonen används av något annat just nu. Försök igen om en stund.'
const MIC_UNAVAILABLE =
	'Ingen åtkomst till mikrofonen. Kontrollera webbläsarens behörigheter.'

function micErrorMessage(name: string, detail: string) {
	if (name === 'NotAllowedError')
		return detail === 'system' ? MIC_OFF_IN_SYSTEM : MIC_NOT_ALLOWED
	if (name === 'NotFoundError' || name === 'OverconstrainedError')
		return MIC_NOT_FOUND
	if (name === 'NotReadableError' || name === 'AbortError') return MIC_BUSY
	return MIC_UNAVAILABLE
}

/** Ett tryck på knappen, från behörighetsfrågan till utfallet. */
type Attempt = {
	number: '1' | '2' | '3+'
	afterHelp: boolean
	/** Inspelningen kom igång - först då betyder tryck/håll något. */
	recorded: boolean
	/** Exakt ett utfall per försök. */
	reported: boolean
}

/**
 * Mikrofonknapp enligt gängse mönster (ChatGPT, Gemini, iOS-diktering):
 *
 * - **Tryck** startar. Inspelningen slutar av sig själv när du tystnat
 *   (~1,5 s), eller när du trycker igen (✓). Ett tryck räcker alltså på mobil.
 * - **Håll in** = prata medan du håller, släpp när du är klar (som
 *   röstmeddelanden). Tillval, inte tvång — tryck fungerar alltid.
 * - **✕ / Esc** avbryter utan att något skickas.
 * - Inget tal inom 5 s, eller bara brus: inget skickas till servern.
 * - Hård maxtid 20 s.
 *
 * Texten lämnas till `onText` och skickas aldrig automatiskt — användaren ser
 * och kan rätta den först (felhörda läkemedelsnamn ska inte bli frågor utan att
 * någon sett dem).
 */
export const VoiceInputButton: FC<{
	onText: (text: string) => void
	className?: string
	/** Vilken kant felrutan fäster mot; välj den som har plats åt rutan. */
	errorAlign?: 'left' | 'right'
	/**
	 * Anropas exakt en gång per försök (för analys). `mode` och `durationMs`
	 * bara när inspelningen startat (inte vid t.ex. `denied`); `text` =
	 * transkriptet, bara vid `transcribed` — anroparen ansvarar för att det
	 * inte lämnar klienten. Övriga fält: försök, hjälptext och felets
	 * råsignaler (IM-128), se `VoiceInputDetails`.
	 */
	onOutcome?: (
		outcome: VoiceInputOutcome,
		mode: 'tap' | 'hold' | undefined,
		details: { durationMs?: number; text?: string } & VoiceInputDetails,
	) => void
}> = ({ onText, className, errorAlign = 'left', onOutcome }) => {
	const [status, setStatus] = useState<Status>('idle')
	const [mode, setMode] = useState<Mode>('toggle')
	const [secondsLeft, setSecondsLeft] = useState(MAX_RECORDING_SECONDS)
	const [level, setLevel] = useState(0)
	const [error, setError] = useState<string | null>(null)

	const recorderRef = useRef<MediaRecorder | null>(null)
	const streamRef = useRef<MediaStream | null>(null)
	const audioCtxRef = useRef<AudioContext | null>(null)
	const rafRef = useRef<number | null>(null)
	const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
	const modeRef = useRef<Mode>('toggle')
	const recordingAtRef = useRef<number | null>(null)
	/** Senaste inspelningens längd, satt i onstop innan mikrofonen släpps. */
	const durationRef = useRef<number | undefined>(undefined)
	const cancelledRef = useRef(false)
	const speechRef = useRef(false)
	const onTextRef = useRef(onText)
	onTextRef.current = onText
	const onOutcomeRef = useRef(onOutcome)
	onOutcomeRef.current = onOutcome
	const statusRef = useRef(status)
	statusRef.current = status
	const attemptRef = useRef<Attempt | null>(null)

	useEffect(() => primeMicPermission(), [])

	// Exakt en gång per försök. 'pending' vid utfallet = fingret var
	// fortfarande nere (maxtid i håll-läge).
	const report = (
		outcome: VoiceInputOutcome,
		text?: string,
		extra?: Pick<VoiceInputDetails, 'failure' | 'stage'>,
		// Försöket utfallet hör till. Nekandet rapporteras först efter en kort
		// behörighetsfråga, och då kan ett nytt tryck redan ha startat nästa.
		attempt = attemptRef.current,
	) => {
		if (attempt?.reported) return
		if (attempt) attempt.reported = true
		onOutcomeRef.current?.(
			outcome,
			attempt?.recorded
				? modeRef.current === 'toggle'
					? 'tap'
					: 'hold'
				: undefined,
			{
				// Avbrutet mitt i inspelningen: onstop har inte gått, räkna härifrån.
				durationMs: attempt?.recorded
					? durationRef.current ??
						(recordingAtRef.current !== null
							? performance.now() - recordingAtRef.current
							: undefined)
					: undefined,
				text,
				attempt: attempt?.number,
				afterHelp: attempt?.afterHelp,
				...extra,
			},
		)
	}
	const reportRef = useRef(report)
	reportRef.current = report

	const setModeBoth = (m: Mode) => {
		modeRef.current = m
		setMode(m)
	}

	const releaseMic = useCallback(() => {
		if (timerRef.current) clearInterval(timerRef.current)
		timerRef.current = null
		if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
		rafRef.current = null
		void audioCtxRef.current?.close().catch(() => {})
		audioCtxRef.current = null
		streamRef.current?.getTracks().forEach(t => t.stop())
		streamRef.current = null
		recordingAtRef.current = null
		setLevel(0)
	}, [])

	// Knappen avmonteras när en fråga börjar köras (AssistantIf !isRunning).
	// Ett transkript som kommer efter det får inte hamna i fältet och bli en
	// del av NÄSTA fråga (`discarded`). Avmontering medan behörighetsfrågan
	// eller inspelningen pågår är i stället `abandoned` (nedan).
	const mountedRef = useRef(true)
	useEffect(() => {
		mountedRef.current = true
		return () => {
			mountedRef.current = false
		}
	}, [])

	useEffect(
		() => () => {
			// Avmontering under frågan eller inspelningen (frågan skickas med
			// Enter): släpp mikrofonen, skicka inget ljud - men räkna försöket.
			if (statusRef.current === 'starting')
				reportRef.current('abandoned', undefined, { stage: 'prompt' })
			if (statusRef.current === 'recording')
				reportRef.current('abandoned', undefined, { stage: 'recording' })
			const rec = recorderRef.current
			if (rec && rec.state !== 'inactive') {
				rec.ondataavailable = null
				rec.onstop = null
				rec.stop()
			}
			releaseMic()
		},
		[releaseMic],
	)

	// Sidan lämnas under frågan eller inspelningen (pagehide; Plausible skickar
	// med keepalive). Ett svar på frågan efteråt räknas inte igen.
	const pending = status === 'starting' || status === 'recording'
	useEffect(() => {
		if (!pending) return
		const onPageHide = () =>
			reportRef.current('abandoned', undefined, {
				stage: statusRef.current === 'recording' ? 'recording' : 'prompt',
			})
		window.addEventListener('pagehide', onPageHide)
		return () => window.removeEventListener('pagehide', onPageHide)
	}, [pending])

	useEffect(() => {
		if (!error) return
		// Hjälptexten behöver längre tid att läsas än ett kort fel.
		const t = setTimeout(
			() => setError(null),
			MIC_HELP.includes(error) ? 15000 : 6000,
		)
		return () => clearTimeout(t)
	}, [error])

	const upload = useCallback(async (blob: Blob) => {
		setStatus('transcribing')
		try {
			const form = new FormData()
			form.append('audio', blob)
			const res = await fetch('/resources/transcribe', {
				method: 'POST',
				body: form,
			})
			const data = (await res.json().catch(() => ({}))) as {
				text?: string
				error?: string
			}
			if (!res.ok) throw new Error(data.error ?? 'Kunde inte tolka ljudet.')
			if (!mountedRef.current) {
				// Frågan hann skickas under transkriberingen: släng transkriptet.
				reportRef.current('discarded')
				return
			}
			if (data.text) {
				onTextRef.current(data.text)
				reportRef.current('transcribed', data.text)
			} else {
				setError(NO_SPEECH)
				reportRef.current('empty')
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Kunde inte tolka ljudet.')
			reportRef.current('error')
		} finally {
			setStatus('idle')
		}
	}, [])

	/** Avslutar inspelningen; `cancel` = släng ljudet. */
	const finish = useCallback((cancel = false) => {
		cancelledRef.current = cancel
		const rec = recorderRef.current
		if (rec && rec.state !== 'inactive') rec.stop()
	}, [])

	const start = useCallback(async () => {
		setError(null)
		setStatus('starting')
		durationRef.current = undefined
		const attempt: Attempt = {
			number: nextMicAttempt(),
			afterHelp: micHelpShownBefore(),
			recorded: false,
			reported: false,
		}
		attemptRef.current = attempt
		const permBefore = currentMicPermission()
		const askedAt = performance.now()
		let stream: MediaStream
		try {
			stream = await navigator.mediaDevices.getUserMedia({
				audio: { echoCancellation: true, noiseSuppression: true },
			})
		} catch (err) {
			const waitBucket = micWaitBucket(performance.now() - askedAt)
			const errorName = micErrorName(err)
			const errorDetail = micErrorDetail(err)
			const message = micErrorMessage(errorName, errorDetail)
			const helpShown = MIC_HELP.includes(message)
			if (mountedRef.current) {
				setStatus('idle')
				setError(message)
				if (helpShown) markMicHelpShown()
			}
			reportRef.current(
				'denied',
				undefined,
				{
					failure: {
						errorName,
						errorDetail,
						permBefore,
						permAfter: await micPermissionAfter(),
						waitBucket,
						helpShown: helpShown && mountedRef.current,
					},
				},
				attempt,
			)
			return
		}
		streamRef.current = stream
		if (!mountedRef.current) {
			// Avmonterad medan behörighetsfrågan visades.
			releaseMic()
			return
		}
		cancelledRef.current = false
		speechRef.current = false

		const mimeType = pickMimeType()
		let rec: MediaRecorder
		try {
			rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
		} catch {
			// Mikrofonen är redan öppen: släpp den, annars lyser den kvar.
			releaseMic()
			setStatus('idle')
			setError('Kunde inte starta inspelningen. Skriv frågan i stället.')
			reportRef.current('error')
			return
		}
		recorderRef.current = rec
		const chunks: Blob[] = []
		rec.ondataavailable = e => {
			if (e.data.size > 0) chunks.push(e.data)
		}
		rec.onstop = () => {
			const startedAt = recordingAtRef.current
			durationRef.current =
				startedAt === null ? undefined : performance.now() - startedAt
			releaseMic()
			const blob = new Blob(chunks, { type: rec.mimeType || mimeType || '' })
			if (cancelledRef.current) {
				setStatus('idle')
				reportRef.current('cancelled')
			} else if (!speechRef.current || blob.size === 0) {
				// Inget tal = inget anrop. Sparar pengar och stoppar modellens
				// benägenhet att hitta på text ur ren tystnad.
				setStatus('idle')
				setError(NO_SPEECH)
				reportRef.current('no_speech')
			} else {
				void upload(blob)
			}
		}

		// Nivåmätning för talstart/tystnad och för ringen runt knappen.
		const vad = createVoiceActivity()
		try {
			const ctx = new AudioContext()
			audioCtxRef.current = ctx
			void ctx.resume().catch(() => {})
			const analyser = ctx.createAnalyser()
			analyser.fftSize = 1024
			ctx.createMediaStreamSource(stream).connect(analyser)
			const buf = new Float32Array(analyser.fftSize)
			const tick = () => {
				analyser.getFloatTimeDomainData(buf)
				let sum = 0
				for (const v of buf) sum += v * v
				const rms = Math.sqrt(sum / buf.length)
				setLevel(Math.min(1, rms * 8))
				const ev = vad.push(rms, performance.now())
				if (ev === 'speech-start') speechRef.current = true
				// Tystnadsstopp bara i tryck-läge; i håll-läge styr fingret.
				if (modeRef.current === 'toggle') {
					if (ev === 'end-of-speech') finish()
					if (ev === 'no-speech') finish()
				}
				rafRef.current = requestAnimationFrame(tick)
			}
			rafRef.current = requestAnimationFrame(tick)
		} catch {
			// Ingen Web Audio: utan nivåmätning kan vi inte avgöra tal, så allt
			// skickas och användaren stoppar själv.
			speechRef.current = true
		}

		rec.start()
		if (attemptRef.current) attemptRef.current.recorded = true
		recordingAtRef.current = performance.now()
		setStatus('recording')
		navigator.vibrate?.(10)
		setSecondsLeft(MAX_RECORDING_SECONDS)
		const startedAt = Date.now()
		timerRef.current = setInterval(() => {
			const left =
				MAX_RECORDING_SECONDS - Math.floor((Date.now() - startedAt) / 1000)
			setSecondsLeft(Math.max(left, 0))
			if (left <= 0) finish()
		}, 250)
	}, [finish, releaseMic, upload])

	// Esc avbryter en pågående inspelning.
	useEffect(() => {
		if (status !== 'recording') return
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') finish(true)
		}
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	}, [status, finish])

	const onPointerDown = (e: PointerEvent<HTMLButtonElement>) => {
		if (e.button !== 0) return
		// Behåll fokus i textfältet (mobiltangentbordet hoppar inte).
		e.preventDefault()
		if (status === 'recording') {
			finish()
			return
		}
		if (status !== 'idle') return
		e.currentTarget.setPointerCapture(e.pointerId)
		setModeBoth('pending')
		void start()
	}

	const onPointerUp = () => {
		if (modeRef.current !== 'pending') return
		const startedAt = recordingAtRef.current
		const heldWhileRecording =
			startedAt !== null && performance.now() - startedAt >= HOLD_MS
		if (heldWhileRecording) {
			setModeBoth('hold')
			finish()
		} else {
			// Kort tryck (eller fingret släpptes under behörighetsfrågan).
			setModeBoth('toggle')
		}
	}

	// Tangentbord (Enter/Mellanslag ger click utan pekarhändelser): tryck-läge.
	const onKeyboardClick = (e: MouseEvent) => {
		if (e.detail !== 0) return
		if (status === 'recording') finish()
		else if (status === 'idle') {
			setModeBoth('toggle')
			void start()
		}
	}

	const recording = status === 'recording'
	const holding = recording && mode === 'pending'
	const label = recording
		? holding
			? 'Släpp när du är klar'
			: 'Klar'
		: status === 'transcribing'
			? 'Tolkar ljudet…'
			: 'Ställ frågan med rösten (tryck, eller håll in medan du pratar)'
	const hint = holding
		? 'Släpp när du är klar'
		: recording
			? 'Lyssnar…'
			: status === 'transcribing'
				? 'Tolkar…'
				: null

	return (
		<div className={cn('relative flex items-center gap-1.5', className)}>
			{recording && (
				<Button
					type="button"
					variant="ghost"
					size="icon"
					className="size-8 rounded-full text-muted-foreground"
					aria-label="Avbryt inspelningen"
					title="Avbryt (Esc)"
					onPointerDown={e => e.preventDefault()}
					onClick={() => finish(true)}
				>
					<XIcon className="size-4" />
				</Button>
			)}
			<span className="relative inline-flex">
				{recording && (
					<span
						aria-hidden="true"
						className="pointer-events-none absolute inset-0 rounded-full bg-primary/25 transition-transform duration-75"
						style={{ transform: `scale(${1 + level * 0.9})` }}
					/>
				)}
				<Button
					type="button"
					variant={recording ? 'default' : 'ghost'}
					size="icon"
					className="relative size-8 touch-none select-none rounded-full [-webkit-touch-callout:none]"
					aria-label={label}
					title={label}
					// Inte disabled under 'starting': en inaktiverad knapp tappar
					// pointerup, och då vet vi inte om fingret släppts (håll-läge).
					disabled={status === 'transcribing'}
					onPointerDown={onPointerDown}
					onPointerUp={onPointerUp}
					onPointerCancel={onPointerUp}
					onClick={onKeyboardClick}
					onContextMenu={e => e.preventDefault()}
				>
					{recording ? (
						<CheckIcon className="size-4" />
					) : status === 'transcribing' || status === 'starting' ? (
						<Loader2Icon className="size-4 animate-spin" />
					) : (
						<MicIcon className="size-4" />
					)}
				</Button>
			</span>
			{hint && (
				<span className="whitespace-nowrap text-xs text-muted-foreground">
					{hint}
					{recording && secondsLeft <= SHOW_COUNTDOWN_AT && (
						<span className="tabular-nums"> · {secondsLeft} s</span>
					)}
				</span>
			)}
			<span role="status" aria-live="polite" className="sr-only">
				{recording
					? 'Lyssnar'
					: status === 'transcribing'
						? 'Tolkar ljudet'
						: ''}
			</span>
			{error && (
				<span
					role="alert"
					className={cn(
						'absolute bottom-full z-10 mb-2 w-64 max-w-[calc(100vw-2rem)] rounded-md border bg-background p-2 text-xs text-destructive shadow-sm',
						errorAlign === 'left' ? 'left-0' : 'right-0',
					)}
				>
					{error}
				</span>
			)}
		</div>
	)
}
